import Reflux from 'reflux';
import SIM from '../../sim/core';
import basketActions from '../../actions/basket';
import storeUtils from '../../utils/store';
import OmnitureHelper from '../../utils/omniture-helper';
import OmnitureUtils from '../../utils/omniture-helper/omniture_utils';
import OmnitureBuilder from '../../utils/omniture-helper/omniture_builder';
import schema from './basket_schema';
import Helpers from './basket_helpers';
import noScroll from '../../utils/noscroll';
import Cookies from 'js-cookie';
import embeddedCheckout from '../../utils/embeddedCheckout';
import QUANTITY_RULES from './quantity_rules_states';

const mapActions = SIM.actions.map;

export default class BasketStore extends Reflux.Store {
  static id = 'basket';

  constructor () {
    super();
    Object.assign(this, Helpers);

    this.state = schema;
    this.load();

    // SEATMAP STORE
    // listen to actions on the SIM map
    mapActions.on(mapActions.EVENTS.SEAT_TOGGLE, this.toggleSeat.bind(this));
    mapActions.on(mapActions.EVENTS.SEAT_REMOVE, this.removeSeat.bind(this));
    mapActions.on(mapActions.EVENTS.SEAT_REMOVE_ALL, this.removeAllSeats.bind(this));
    mapActions.on(mapActions.EVENTS.SECTION_RESET, this.resetGASectionTicketQuantity.bind(this));
    mapActions.on(mapActions.EVENTS.LOAD_ERROR, this.simLoadError.bind(this));
    this.listenTo(basketActions.updateSeatPrice, 'updateSeatPrice');
    this.listenTo(basketActions.priceTypeToUnassignedSeat, 'priceTypeToUnassignedSeat');
    this.listenTo(basketActions.updateGASection, 'updateGASection');
    this.listenTo(basketActions.decreaseGASection, 'decreaseGASection');
    this.listenTo(basketActions.updateGASectionTicketQuantity, 'updateGASectionTicketQuantity');
    this.listenTo(basketActions.resetGASectionTicketQuantity, 'resetGASectionTicketQuantity');
    this.listenTo(basketActions.resetSelectSeatAttempt, 'resetSelectSeatAttempt');
    this.listenTo(basketActions.unlockPriceType, 'unlockPriceType');
    this.listenTo(basketActions.unlockPriceType.error, 'unlockPriceTypeError');
    this.listenTo(basketActions.unlockPriceType.success, 'unlockPriceTypeSuccess');
    this.listenTo(basketActions.clearUnlockPriceTypePasswordErrors, 'clearUnlockPriceTypePasswordErrors');
    this.listenTo(basketActions.dismissSingleSeatModal, 'dismissSingleSeatModal');

    // BASKET STORE
    this.listenTo(basketActions.toggleShowTickets, 'toggleShowTickets');
    this.listenTo(basketActions.hideRecaptcha, 'hideRecaptcha');
    this.listenTo(basketActions.showRecaptcha, 'showRecaptcha');
    this.listenTo(basketActions.prepareToBuy, 'prepareToBuy');
    this.listenTo(basketActions.dismissError, 'dismissError');
    this.listenTo(basketActions.buyTicket, 'buyTicket');
    this.listenTo(basketActions.buyTicket.error, 'buyTicketError');
    this.listenTo(basketActions.goToBestAvailable, 'goToBestAvailable');
  }

  load () {
    const eventConfig = storeUtils.get('event_config');
    const eventPrices = storeUtils.get('event_prices');
    const errorInfo = storeUtils.get('error_info');
    const reCaptcha = storeUtils.get('reCaptcha');
    const unocFee = storeUtils.get('unocFee');

    // filter out upsells with ONLY_ONE aka. 'one per order' quantity rule
    if (eventPrices.mandatoryUpsells) {
      eventPrices.mandatoryUpsells = eventPrices.mandatoryUpsells.filter(
        mu => mu.quantityRule !== QUANTITY_RULES.ONLY_ONE
      );
    }

    const data = {
      // get the configurations the basket needs
      config:    eventConfig,
      error:     errorInfo,
      reCaptcha: {...this.state.reCaptcha, ...reCaptcha},
      // Fetch only the data this store is interested in
      priceCategories          : eventPrices.priceCategories,
      prices                   : eventPrices.prices,
      priceTypes               : eventPrices.priceTypes,
      areaNames                : eventPrices.areaNames,
      currency                 : eventPrices.currency,
      moneyFormat              : eventPrices.moneyFormat,
      showServiceFeeBreakdown  : eventPrices.showServiceFeeBreakdown,
      showDeliveryFees         : eventPrices.showDeliveryFees,
      mandatoryUpsells         : eventPrices.mandatoryUpsells,
      feeRollup                : eventPrices.feeRollup,
      urls                     : eventConfig.urls && eventConfig.urls.checkout,
      eventIneligibilityCause  : eventConfig.eventIneligibilityCause,
      seatmapOverviewMode      : eventConfig.seatmapDisplayOverviewMode,
      showUnocFee              : unocFee.showUnocFee,
      unocFeeTotal             : unocFee.unocFeeTotal
    };

    /**
     * price.priceCategory comes from the server as a number
     * priceCategories.key is a string
     * when comparing
     */
    data.prices = (eventPrices.prices || []).map(price => {
      price.priceCategory = price.priceCategory.toString();
      price.priceType     = price.priceType.toString();
      return price;
    });

    this.setState(data);
  }

  done () {
    this.calculateTotal();
  }

// ERRORS -----------------------------------------------------------

  simLoadError () {
    this.setState({
      error: {...this.state.error, errorCode: OmnitureUtils.LOAD_ERROR }
    });
    OmnitureBuilder
      .errorMessage(OmnitureUtils.getOmnitureErrorMessage(OmnitureUtils.LOAD_ERROR))
      .sendSingle();
    this.done();
  }

  dismissSingleSeatModal () {
    this.setState({attemptedToBuyTicket: false});
    this.done();
  }

  dismissError () {
    noScroll.off();
    this.setState({
      error: {...this.state.error, errorCode: null }
    });
    this.done();
  }

  goToBestAvailable (eventId) {
    Cookies.set(`EDP_TYPE_${eventId}`, 'CLASSIC');

    if (embeddedCheckout.isEnabled()) {
      embeddedCheckout.collapseFullScreen();
      // Add delay to propagate the callback action to the 3rd party
      setTimeout(
        function () {
          window.location.reload();
        },
        1000);
    }
    else {
      window.location.reload();
    }
  }

// BASKET -----------------------------------------------------------

  prepareToBuy (basket) {
    this.setState({hasSingleSeat: SIM.hasSingleSeat()});
    if (this.state.config.avoidSingleSeats && this.state.hasSingleSeat) {
      this.setState({attemptedToBuyTicket: true});
    }
    else if (this.state.reCaptcha.isEnabled && !this.state.reCaptcha.wasCompleted) {
      this.setState({
        reCaptcha: {...this.state.reCaptcha, recaptchaCompleted: true, showRecaptcha: true }
      });
      OmnitureBuilder.reCaptchaPageView().sendSingle();
    }
    else {
      basketActions.buyTicket(basket);
    }
    this.done();
  }

  buyTicket () {
    this.setState({isBuying: true});
    this.done();
  }

  buyTicketError (err, resp) {
    this.setState({
      isBuying: false,
      error: {...this.state.error, errorCode: resp.errorCode, payload: resp.payload}
    });
    OmnitureBuilder
      .errorMessage(OmnitureUtils.getOmnitureErrorMessage(this.state.error.errorCode))
      .sendSingle();

    if (process.env.NODE_ENV === 'development') {
      console.error('purchase error,\n' + JSON.stringify(err) + '\n' + JSON.stringify(resp));
    }
    this.done();
  }

  hideRecaptcha () {
    this.setState({
      reCaptcha: {...this.state.reCaptcha, showRecaptcha: false }
    });
    this.done();
  }

  showRecaptcha () {
    this.setState({
      reCaptcha: {...this.state.reCaptcha, showRecaptcha: true }
    });
    this.done();
  }

  toggleShowTickets () {
    this.setState({ showTickets: !this.state.showTickets });
    this.done();
  }

// SEATMAP Seated Sections ------------------------------------------

  toggleSeat (seat) {
    // here we are unselecting the "unassigned seat"
    if (this.state.unassignedSeat && this.state.unassignedSeat.id === seat.id) {
      this.setState({unassignedSeat: null });
      this.done();
      return;
    }

    // here we are deselecting a seat that has a stub in the basket
    if (this.state.activeSeats[seat.id]) {
      const currentActiveSeats = {...this.state.activeSeats};
      delete currentActiveSeats[seat.id];
      this.setState({activeSeats: currentActiveSeats});
      this.done();
      return;
    }

    this.setState({
      hasSingleSeat: SIM.hasSingleSeat(),
      unassignedSeat: {...seat}
    });

    // if the basket is full remove the active state (colour fill) from the seat
    // and dont add the ticket to the basket
    if (this.state.count === this.state.config.ticketLimit) {
      this.setState({attemptedToSelectSeat: true});
      mapActions.emit(mapActions.EVENTS.SEAT_REMOVE, seat.id);
      this.done();
      return;
    }

    // try guessing the priceType
    this.tryAssigningPriceType();
    this.done();
  }

  removeSeat (seatId) {
    // here we are unselecting the "unassigned seat"
    if (this.state.unassignedSeat && this.state.unassignedSeat.id === seatId) {
      this.setState({unassignedSeat: null});
    }
    // here we are deselecting a seat that has a stub in the basket
    if (this.state.activeSeats[seatId]) {
      const currentActiveSeats = {...this.state.activeSeats};
      delete currentActiveSeats[seatId];
      this.setState({activeSeats: currentActiveSeats});
    }
    this.done();
  }

  removeAllSeats () {
    this.setState({ unassignedSeat: null, activeSeats: {} });
    this.done();
  }

  updateSeatPrice (seatId, newPriceType) {
    var seat = this.state.activeSeats[seatId];
    if (seat) {
      this.setState({
        activeSeats: {
          ...this.state.activeSeats,
          [seatId]: {...seat, priceType: newPriceType}
        }
      });
      this.done();
    }
  }

  // Seated reset selection, called on closing the modal
  resetSelectSeatAttempt () {
    this.setState({attemptedToSelectSeat: false});
    this.resetPasswordProtectedUnlockingErrors();
    this.done();
  }

  priceTypeToUnassignedSeat (priceType, disableNoScroll = true) {
    if (disableNoScroll) {
      noScroll.off();
    }
    // If there is no seat or if the ticketLimit is already reached do not
    // add the ticket to the basket
    var seat = this.state.unassignedSeat;
    if (
      !seat ||
      this.state.count === this.state.config.memberTicketLimit ||
      this.state.count === this.state.config.ticketLimit
    ) {
      this.done();
      return;
    }
    // create the stub
    var opts = {
      id:            seat.id,
      row:           seat.row,
      seat:          seat.seat,
      priceCategory: seat.priceLevel,
      priceType:     priceType
    };

    this.setState({
      unassignedSeat: null,
      activeSeats: {
        ...this.state.activeSeats,
        [seat.id]: this.createStub('SEAT', opts)
      }
    });
    this.done();
  }

// SEATMAP GA Sections ----------------------------------------------

  updateGASectionTicketQuantity (categoryId, priceTypeId, newValue) {
    this.setState({
      sectionBasket: {
        ...this.state.sectionBasket,
        [categoryId]: {
          ...this.state.sectionBasket[categoryId] || {},
          [priceTypeId]: {'qty': newValue}
        }
      }
    });
    this.done();
  }

  // GA Reset Selection, called on closing the modal
  resetGASectionTicketQuantity () {
    this.setState({sectionBasket: {}});
    this.resetPasswordProtectedUnlockingErrors();
    this.done();
  }

  // Called from the GA Modal Selector
  // Loops over the categories, price types and add the quantity to the basket
  updateGASection (section, GATickets) {
    noScroll.off();
    var categoryIds = Object.keys(GATickets);

    categoryIds.forEach(function (categoryID) {
      var categoryPriceTypes = GATickets[categoryID];
      var priceTypeIds = Object.keys(categoryPriceTypes);

      priceTypeIds.forEach(function (priceTypeID) {
        var quantity = categoryPriceTypes[priceTypeID].qty;
        this.updateGATicketQuantity(section, categoryID, priceTypeID, quantity);
      }, this);
    }, this);
    basketActions.resetGASectionTicketQuantity();
    this.done();
  }

  // Decrease quantity or delete a GA ticket from the basket
  decreaseGASection (sectionId, priceCategory, priceType) {
    var GATicket = this.state.GASeats[sectionId][priceCategory][priceType];
    // Create/Update the GA Ticket here
    if (GATicket.qty > 1) {
      this.setState({
        GASeats: {
          ...this.state.GASeats,
          [sectionId]: {
            ...this.state.GASeats[sectionId],
            [priceCategory]: {
              ...this.state.GASeats[sectionId][priceCategory],
              [priceType]: {
                ...GATicket,
                qty: GATicket.qty - 1
              }
            }
          }
        }
      });
      const ticket = this.state.quantityInSections[sectionId][priceCategory][priceType];
      if (ticket.qty > 1) {
        this.setState({
          quantityInSections: {
            ...this.state.quantityInSections,
            [sectionId]: {
              ...this.state.quantityInSections[sectionId],
              [priceCategory]: {
                ...this.state.quantityInSections[sectionId][priceCategory],
                [priceType]: {
                  ...ticket,
                  qty: ticket.qty - 1
                }
              }
            }
          }
        });
      }
      return this.done();
    }
    else {
      const currentGASeats = {...this.state.GASeats};
      const currentQuantityInSections = {...this.state.quantityInSections};
      delete currentQuantityInSections[sectionId][priceCategory][priceType];
      delete currentGASeats[sectionId][priceCategory][priceType];
      this.setState({GASeats: currentGASeats, quantityInSections: currentQuantityInSections});
    }

    // clean the GASeats object
    this.cleanGAObject(sectionId, priceCategory);
    this.done();
  }

// Unlocking Password Protected Price Types -------------------------

  unlockPriceType (data) {
    this.setState({ isUnlockingPriceType: data.priceTypeId });
    this.done();
  }

  unlockPriceTypeError (resp) {
    this.setState({
      isUnlockingPriceType: false,
      priceTypes: {
        ...this.state.priceTypes,
        [resp.priceTypeId]: {
          ...this.state.priceTypes[resp.priceTypeId],
          unlockingErrors: resp
        }
      }
    });

    // sending the error to Omniture
    let omnitureValue = null;
    switch (resp.error) {
      case 'invalidPassword':
        omnitureValue = 'EDP - Price - Invalid Password';
        break;
      case 'usedPassword':
        omnitureValue = 'EDP Password - Price - No Tix available';
        break;
      case 'promotionNotStarted':
        omnitureValue = 'EDP Password - Price - Promotion not started';
        break;
      default:
        omnitureValue = 'EDP Password - Price - Problems validating';
    }
    OmnitureHelper.sendSingle({ prop18: omnitureValue});

    this.done();
  }

  unlockPriceTypeSuccess (resp) {
    this.setState({
      priceTypeJustUnlocked: this.state.isUnlockingPriceType,
      isUnlockingPriceType: false,
      priceTypes: {
        ...this.state.priceTypes,
        [resp.priceTypeId]: {
          ...this.state.priceTypes[resp.priceTypeId],
          unlockingErrors: null,
          isUnlocked: true
        }
      }
    });
    this.done();
  }

  clearUnlockPriceTypePasswordErrors (priceTypeId) {
    this.setState({
      priceTypes: {
        ...this.state.priceTypes,
        [priceTypeId]: {
          ...this.state.priceTypes[priceTypeId],
          unlockingErrors: null
        }
      }
    });
    this.done();
  }
}



// WEBPACK FOOTER //
// ./frontend/react/stores/basket/index.js